import * as Cmp from "@impero-com/common/utils/comparison";
import {
    formatDateTimeAndTimezone,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    UsePresetTableSearchParamsReturns,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFacetedUniqueValues,
    getSortedRowModel,
    useReactTable,
    getFilteredRowModel,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";
import { Link } from "react-router-dom";

import {
    TestingActivityCurrentStep,
    EffectivenessFieldTypeAnswer,
    IanaTimezoneView,
    TActListRelRes,
    TActTableRow,
} from "api/generated/model";
import { FilterBarDateTimeRange } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRange";
import { DateTimeRangeFilterValue } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeTypes";
import { dateTimeRangeTSTFilterFn } from "components/table/FilterBarDateTimeRange/FilterBarDateTimeRangeUtils";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { translateMonth } from "org/control/list/utils";
import { isActivityOverdue } from "org/controlTesting/overdueUtils";
import { buildTestingActivityViewEditUrl } from "org/controlTesting/urlUtils";
import {
    formatTagsAndCategoriesString,
    tagAndCategoryListDefaultTSTCell,
    tagsTSTFilterFn,
} from "org/tags/tagsTSTUtils";
import { monthComparison } from "utils/monthComparison";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    buildTestingActivityListTableStateStorageKey,
    TESTING_ACTIVITY_LIST_COLUMN_IDS as ID,
    testingActivityListColumnFiltersCodecMap,
    testingActivityListDataParamsCodecMap,
} from "./consts";

type TestingActivityListColumnFilters = ZodCodecMapToType<
    typeof testingActivityListColumnFiltersCodecMap
>;

export type TestingActivityListSearchParams = UsePresetTableSearchParamsReturns<
    typeof testingActivityListDataParamsCodecMap
>;

interface UseTestingActivityListTableProps {
    data: TActTableRow[];
    timezoneName: IanaTimezoneView;
    relatedResources: TActListRelRes;
    testingProgramId: number;
    searchParams: TestingActivityListSearchParams;
}
export function useTestingActivityListTable({
    data,
    timezoneName,
    relatedResources,
    testingProgramId,
    searchParams,
}: UseTestingActivityListTableProps) {
    const { t } = useTranslation();

    const columns = buildColumns(t, timezoneName, relatedResources);
    const tableId =
        buildTestingActivityListTableStateStorageKey(testingProgramId);

    const table = useReactTable({
        state: searchParams.state,
        onStateChange: searchParams.onStateChange,
        data,
        columns,
        enableRowSelection: true,
        initialState: {
            // useTableSearchParams doesn't support all initial states - supported states should be declared only there.
            // columnPinning is seeded (from the URL or its default) by the preset hook's initialState.
            ...searchParams.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        defaultColumn: {
            size: TST_COLUMN_SIZE.L,
        },
        enablePinning: true,
        enableColumnPinning: true,
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<TActTableRow>(),
        getSortedRowModel: getSortedRowModel<TActTableRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<TActTableRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<TActTableRow>(),
        getFilteredRowModel: getFilteredRowModel<TActTableRow>(),
        getPaginationRowModel: getPaginationRowModel<TActTableRow>(),
    });

    const filterComponent = (
        column: Column<TActTableRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, relatedResources);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<TActTableRow>) =>
            column.id === ID.TAGS || column.id === ID.ENTITIES_TO_TEST,
        tableId,
    };
}

const columnHelper = createColumnHelper<TActTableRow>();

const buildColumns = (
    t: TFunction,
    timezoneName: IanaTimezoneView,
    relatedResources: TActListRelRes
) => [
    columnHelper.display({
        id: TST_BLESSED_ID_COLUMN_SELECTION,
        cell: (info) => {
            const onClick = () => {
                info.row.toggleSelected();
            };
            return (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={onClick}
                />
            );
        },
        header: (info) => {
            return (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            );
        },
        size: TST_COLUMN_SIZE.CHECKBOX,
        meta: { fixedWidth: true },
    }),
    columnHelper.accessor((row) => row.title, {
        id: ID.TITLE,
        enableColumnFilter: false,
        cell: (info) => (
            <Link
                to={buildTestingActivityViewEditUrl(
                    info.row.original.activityId
                )}
            >
                {info.getValue()}
            </Link>
        ),
        enableSorting: true,
        enableHiding: false,
        sortingFn: "alphanumericCaseSensitive",
        header: () => t("TestingActivityListForTestingProgram_NameColumn"),
        size: TST_COLUMN_SIZE.XL,
    }),
    columnHelper.accessor(
        (row) =>
            formatTagsAndCategoriesString(row.samplingTags, {
                tags: relatedResources.tags,
                tagCategories: relatedResources.tagCategories,
            }),
        {
            id: ID.ENTITIES_TO_TEST,
            cell: (info) =>
                tagAndCategoryListDefaultTSTCell(
                    info.row.original.samplingTags,
                    relatedResources.tags,
                    relatedResources.tagCategories
                ),
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode((row, value: string[]) =>
                tagsTSTFilterFn(row.original.samplingTags, value)
            ),
            header: () =>
                t("TestingActivityListForTestingProgram_EntitiesToTestColumn"),
        }
    ),
    columnHelper.accessor(
        (row) =>
            formatTagsAndCategoriesString(row.tags, {
                tags: relatedResources.tags,
                tagCategories: relatedResources.tagCategories,
            }),
        {
            id: ID.TAGS,
            cell: (info) =>
                tagAndCategoryListDefaultTSTCell(
                    info.row.original.tags,
                    relatedResources.tags,
                    relatedResources.tagCategories
                ),
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode((row, value: string[]) =>
                tagsTSTFilterFn(row.original.tags, value)
            ),
            header: () => t("TestingActivityListForTestingProgram_TagsColumn"),
        }
    ),
    columnHelper.accessor((row) => row.dueDate, {
        id: ID.DUE_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map((row) => row.original.dueDate, Cmp.primitive),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(value, row.original.dueDate)
        ),
        header: () => t("TestingActivityListForTestingProgram_DueDateColumn"),
        size: TST_COLUMN_SIZE.L,
    }),
    columnHelper.accessor(
        (row) =>
            formatUserName(
                relatedResources.users.find((u) => u.id === row.responsible)
            ),
        {
            id: ID.USER_RESPONSIBLE,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, ID.USER_RESPONSIBLE, value)
            ),
            header: () =>
                t("TestingActivityListForTestingProgram_ResponsibleColumn"),
            size: TST_COLUMN_SIZE.L,
        }
    ),
    columnHelper.accessor((row) => row.effectiveness ?? "", {
        id: ID.EFFECTIVENESS,
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: "alphanumericCaseSensitive",
        filterFn: createFilterFnWithMode((row, value: string[]) =>
            value.includes(row.getValue(ID.EFFECTIVENESS))
        ),
        header: () =>
            t("TestingActivityListForTestingProgram_EffectivenessColumn"),
        size: TST_COLUMN_SIZE.L,
    }),
    columnHelper.accessor((row) => getTestingActivityListStatusLabel(row, t), {
        id: ID.STATUS,
        cell: (info) => info.getValue(),
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: createFilterFnWithMode((row, value: string[]) =>
            value.includes(row.getValue(ID.STATUS))
        ),
        sortingFn: "alphanumericCaseSensitive",
        header: () => t("TestingActivityListForTestingProgram_StatusColumn"),
        size: TST_COLUMN_SIZE.L,
    }),
    columnHelper.accessor(
        (row) => ({
            month: row.samplingPeriod.periodStartMonth,
            year: row.samplingPeriod.periodStartYear,
        }),
        {
            id: ID.SAMPLING_PERIOD_START,
            cell: (info) => {
                const { month, year } = info.getValue();
                return `${translateMonth(month)} ${year}`;
            },
            enableSorting: true,
            enableColumnFilter: false,
            sortingFn: Cmp.chain([
                Cmp.map(
                    (row) => row.original.samplingPeriod.periodStartYear,
                    Cmp.primitive
                ),
                Cmp.map(
                    (row) => row.original.samplingPeriod.periodStartMonth,
                    monthComparison()
                ),
            ]),
            header: () =>
                t(
                    "TestingActivityListForTestingProgram_SamplingPeriodStartColumn"
                ),
        }
    ),
    columnHelper.accessor(
        (row) => ({
            month: row.samplingPeriod.periodEndMonth,
            year: row.samplingPeriod.periodEndYear,
        }),
        {
            id: ID.SAMPLING_PERIOD_END,
            cell: (info) => {
                const { month, year } = info.getValue();
                return `${translateMonth(month)} ${year}`;
            },
            enableSorting: true,
            enableColumnFilter: false,
            sortingFn: Cmp.chain([
                Cmp.map(
                    (row) => row.original.samplingPeriod.periodEndYear,
                    Cmp.primitive
                ),
                Cmp.map(
                    (row) => row.original.samplingPeriod.periodEndMonth,
                    monthComparison()
                ),
            ]),
            header: () =>
                t(
                    "TestingActivityListForTestingProgram_SamplingPeriodEndColumn"
                ),
        }
    ),
    columnHelper.accessor((row) => row.responsibleInfo.dueDate, {
        id: ID.RESPONSIBLE_DUE_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.responsibleInfo.dueDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.responsibleInfo.dueDate
                )
        ),
        header: () =>
            t("TestingActivityListForTestingProgram_ResponsibleDueDateColumn"),
    }),
    columnHelper.accessor(
        (row) =>
            formatUserName(
                relatedResources.users.find(
                    (u) => u.id === row.firstReviewInfo?.user
                )
            ) ?? "",
        {
            id: ID.FIRST_REVIEWER,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, ID.FIRST_REVIEWER, value)
            ),
            header: () =>
                t("TestingActivityListForTestingProgram_FirstReviewerColumn"),
        }
    ),
    columnHelper.accessor((row) => row.firstReviewInfo?.dueDate ?? "", {
        id: ID.FIRST_REVIEWER_DUE_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.firstReviewInfo?.dueDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.firstReviewInfo?.dueDate ?? ""
                )
        ),
        header: () =>
            t(
                "TestingActivityListForTestingProgram_FirstReviewerDueDateColumn"
            ),
    }),
    columnHelper.accessor(
        (row) =>
            formatUserName(
                relatedResources.users.find(
                    (u) => u.id === row.secondReviewInfo?.user
                )
            ) ?? "",
        {
            id: ID.SECOND_REVIEWER,
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode((row, value: string[] | null) =>
                tstArrIncludesSomeFilterFn(row, ID.SECOND_REVIEWER, value)
            ),
            header: () =>
                t("TestingActivityListForTestingProgram_SecondReviewerColumn"),
        }
    ),
    columnHelper.accessor((row) => row.secondReviewInfo?.dueDate ?? "", {
        id: ID.SECOND_REVIEWER_DUE_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.secondReviewInfo?.dueDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.secondReviewInfo?.dueDate ?? ""
                )
        ),
        header: () =>
            t(
                "TestingActivityListForTestingProgram_SecondReviewerDueDateColumn"
            ),
    }),
    columnHelper.accessor((row) => row.responsibleInfo.completionDate ?? "", {
        id: ID.SUBMITTED_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.responsibleInfo.completionDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.responsibleInfo.completionDate ?? ""
                )
        ),
        header: () =>
            t("TestingActivityListForTestingProgram_SubmittedDateColumn"),
    }),
    columnHelper.accessor((row) => row.firstReviewInfo?.completionDate ?? "", {
        id: ID.REVIEWED_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.firstReviewInfo?.completionDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.firstReviewInfo?.completionDate ?? ""
                )
        ),
        header: () =>
            t("TestingActivityListForTestingProgram_ReviewedDateColumn"),
    }),
    columnHelper.accessor((row) => row.secondReviewInfo?.completionDate ?? "", {
        id: ID.SECOND_REVIEWED_DATE,
        cell: (info) =>
            formatDateTimeAndTimezone(info.getValue(), timezoneName),
        enableSorting: true,
        enableColumnFilter: true,
        enableGlobalFilter: false,
        sortingFn: Cmp.map(
            (row) => row.original.secondReviewInfo?.completionDate,
            Cmp.primitive
        ),
        filterFn: createFilterFnWithMode(
            (row, value: DateTimeRangeFilterValue) =>
                dateTimeRangeTSTFilterFn(
                    value,
                    row.original.secondReviewInfo?.completionDate ?? ""
                )
        ),
        header: () =>
            t("TestingActivityListForTestingProgram_SecondReviewedDateColumn"),
    }),
    columnHelper.accessor((row) => row.description, {
        id: ID.DESCRIPTION,
        enableColumnFilter: false,
        cell: (info) => info.row.original.description,
        enableSorting: true,
        sortingFn: "alphanumericCaseSensitive",
        header: () =>
            t("TestingActivityListForTestingProgram_DescriptionColumn"),
    }),
];

function getEffectivenessFilterLabel(key: string, t: TFunction) {
    if (key === "") {
        return t(
            "TestingProgramView_Effectiveness_NoEffectivenessSpecifiedOption"
        );
    }
    return key;
}

function getFilterComponent(
    t: TFunction,
    column: Column<TActTableRow>,
    target: FilterTarget,
    relatedResources: TActListRelRes
) {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingActivityListColumnFilters>(column);
    const columnId = column.id as keyof TestingActivityListColumnFilters;

    switch (columnId) {
        case ID.SAMPLING_PERIOD_START:
            throw new Error("sampling period filtering not implemented");
        case ID.SAMPLING_PERIOD_END:
            throw new Error("sampling period filtering not implemented");
        case ID.ENTITIES_TO_TEST: {
            const uniqueTagIds = getUniqueTagIds(column, "samplingTags");
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={relatedResources.tagCategories}
                    tags={relatedResources.tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_EntitiesToTestFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case ID.TAGS: {
            const uniqueTagIds = getUniqueTagIds(column, "tags");
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={relatedResources.tagCategories}
                    tags={relatedResources.tags}
                    uniqueTagIds={uniqueTagIds}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_TagsFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        case ID.DUE_DATE:
        case ID.RESPONSIBLE_DUE_DATE:
        case ID.FIRST_REVIEWER_DUE_DATE:
        case ID.SECOND_REVIEWER_DUE_DATE:
        case ID.SUBMITTED_DATE:
        case ID.REVIEWED_DATE:
        case ID.SECOND_REVIEWED_DATE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <FilterBarDateTimeRange
                    filterValue={value}
                    setFilterValue={setFilterValue}
                    facetedValues={column.getFacetedUniqueValues()}
                />
            );
        }
        case ID.USER_RESPONSIBLE: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={getUserFilterOptions(column, t)}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_ResponsibleFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.FIRST_REVIEWER: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={getUserFilterOptions(column, t)}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_FirstReviewerFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.SECOND_REVIEWER: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={getUserFilterOptions(column, t)}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_SecondReviewerFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.EFFECTIVENESS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<EffectivenessFieldTypeAnswer | "", number> =
                column.getFacetedUniqueValues();
            const options = [...values.keys()].map((status) => ({
                label: getEffectivenessFilterLabel(status, t),
                value: status,
            }));

            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_EffectivenessFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case ID.STATUS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<TestingActivityCurrentStep, number> =
                column.getFacetedUniqueValues();
            const labels = [...values.keys()];
            const options = labels.map((label) => ({ label, value: label }));

            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "TestingActivityListForTestingProgram_StatusFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            assertNever(columnId);
    }
}

function getTestingActivityListStatusLabel(row: TActTableRow, t: TFunction) {
    const isOverdue = isActivityOverdue(row);
    switch (row.status) {
        case "responsible": {
            return isOverdue
                ? t("TestingProgramView_Status_WaitOnResponsibleOverdue")
                : t("TestingProgramView_Status_WaitOnResponsible");
        }
        case "firstReviewer": {
            return isOverdue
                ? t("TestingProgramView_Status_WaitOnFirstReviewerOverdue")
                : t("TestingProgramView_Status_WaitOnFirstReviewer");
        }
        case "secondReviewer": {
            return isOverdue
                ? t("TestingProgramView_Status_WaitOnSecondReviewerOverdue")
                : t("TestingProgramView_Status_WaitOnSecondReviewer");
        }
        case "completed":
            return t("TestingProgramView_Status_Completed");
        default:
            assertNever(row.status);
    }
}

function getUniqueTagIds(
    column: Column<TActTableRow>,
    tagsOriginalKey: "samplingTags" | "tags"
) {
    const rows = column.getFacetedRowModel();
    const tagIdsPerRow = rows.flatRows.map(
        (row) => row.original[tagsOriginalKey]
    );
    const uniqueTagIds = Array.from(new Set(tagIdsPerRow.flat()));
    return uniqueTagIds;
}
function getUserFilterOptions(column: Column<TActTableRow>, t: TFunction) {
    const values = column.getFacetedUniqueValues();
    const options = [...values.keys()].map((user) =>
        user === ""
            ? {
                  label: t("TestingProgramView_Effectiveness_NoAssigneeOption"),
                  value: "",
              }
            : { label: user, value: user }
    );
    return options;
}
